import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import { usaStates } from "../../data/USStates";
import { alpha } from "@mui/material/styles";
import EmergencyOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useCerberus } from "../../hooks/useCerberus";
import { useEnabledModules } from "../../modules/ModulesProvider";
import RegistrationMedicalHistory from "../Registration/Patient/RegistrationMedicalHistory";
import RegistrationMedications from "../Registration/Patient/RegistrationMedications";
import useSubscriptionPlans from "../../hooks/useSubscriptionPlans";
import SessionService from "../../services/SessionService";
import PlanPicker, { defaultPlanId } from "../Subscription/PlanPicker";
import ChoiceCard from "./ChoiceCard";
import DocumentReviewDialog from "./DocumentReviewDialog";
import DocumentUpdatesStep from "./DocumentUpdates";
import EnrollmentShell, { BackLink, BusyOverlay, StepActions, StepHeading, primaryButtonSx } from "./EnrollmentShell";
import ReviewAndSign from "./ReviewAndSign";
import {
  ConsentDocument,
  EnrollmentContact,
  EnrollmentError,
  EnrollmentReview,
  EnrollmentState,
  SmsDisclosure,
  enrollmentApi,
  openPdf,
} from "./enrollmentApi";

const STEPS = ["About you", "Paperwork", "Review & sign", "Payment", "Health history"];

/** Where the server's state puts the patient. Health history follows payment, until registered. */
const stepFor = (state: EnrollmentState | null, isRegistered: boolean): number | null => {
  if (state?.status === "Active" && !isRegistered) return 4;
  switch (state?.status) {
    case undefined:
      return null;
    case "EligibilityConfirmed":
      return 1;
    case "ElectronicPaperworkAgreed":
      return 2;
    case "CountersignPending":
    case "FullyExecuted":
    case "Active":
      return 3;
    default:
      return 0;
  }
};

/**
 * TeleHeadache Review & Sign enrollment (consent package 2026-10-04). Rendered by RootLayout in
 * place of the app whenever identity says `enrollmentRequired` — like the MFA setup screen, the
 * only ways out are finishing or signing out. Every step's state lives on the server, so a reload,
 * another device or a return from Stripe resumes where the patient left off.
 */
export default function EnrollmentScreen() {
  const { refreshUserData, cerberusUser, isRegistered, enrollmentRequired } = useCerberus();
  const [state, setState] = useState<EnrollmentState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // A step the patient went Back to. Null = the step the server says they are on. Only steps
  // before signing can be revisited; once signed there is nothing to change.
  const [viewStep, setViewStep] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await enrollmentApi.state();
      setState(next);
      // Re-read identity ONLY while it is stale (still says enrollment is required). A refresh
      // remounts this screen — the app shows nothing while identity loads — and the remount loads
      // again; refreshing unconditionally on Active was an endless loop once the health-history
      // step kept paid members here.
      // CONS-4: "no longer required" rather than "Active" — an Active member can be held here by a
      // revised document, and refreshing while still held would loop the same way.
      if (!next.required && enrollmentRequired) await refreshUserData();
    } catch (e) {
      setLoadError((e as EnrollmentError).message);
    }
  }, [refreshUserData, enrollmentRequired]);

  useEffect(() => {
    void load();
  }, [load]);

  // CONS-4: a member past the due date for a revised document signs it here, outside the steps.
  const updatesDue = state?.status === "Active" && state.documentUpdates?.blocking ? state.documentUpdates : null;
  const serverStep = updatesDue ? null : stepFor(state, isRegistered);
  const step = viewStep !== null && serverStep !== null && viewStep < serverStep && serverStep < 3 ? viewStep : serverStep;

  // Each step starts at the top, not wherever the last one was scrolled to.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  /** A step finished: show whatever the server says comes next. */
  const advance = (next: EnrollmentState) => {
    setViewStep(null);
    setState(next);
  };
  const back = (to: number) => () => setViewStep(to);

  const signOut = async () => {
    await SessionService.getInstance().logout();
    window.location.href = "/";
  };

  return (
    <EnrollmentShell
      step={step}
      steps={STEPS}
      firstName={cerberusUser.firstName}
      isTest={!!state?.isTestConfiguration}
      onSignOut={signOut}
    >
      {loadError && <Alert severity="error">{loadError}</Alert>}
      {!state && !loadError && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress aria-label="Loading" />
        </Box>
      )}
      {state && updatesDue && <DocumentUpdatesStep updates={updatesDue} onAccepted={() => void load()} />}
      {state && step === 0 && (
        <EligibilityStep
          firstName={cerberusUser.firstName}
          initial={state.contact}
          onDone={advance}
          supportContact={state.supportContact}
        />
      )}
      {state && step === 1 && <ElectronicPaperworkStep onDone={advance} onBack={back(0)} />}
      {state && step === 2 && (
        <PlanAndSignStep
          onSigned={() => {
            setViewStep(null);
            void load();
          }}
          onBack={back(1)}
        />
      )}
      {state && step === 3 && <PaymentStep state={state} onRefresh={load} />}
      {state && step === 4 && <HealthHistoryStep contact={state.contact} />}
    </EnrollmentShell>
  );
}

/** A quiet bordered panel used for secondary content inside a step. */
function Panel({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "warning" }) {
  return (
    <Box
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: tone === "warning" ? alpha(theme.palette.error.main, 0.35) : "divider",
        bgcolor: tone === "warning" ? alpha(theme.palette.error.main, 0.04) : "background.paper",
      })}
    >
      {children}
    </Box>
  );
}

// ---- 1. about you (contact + eligibility) ---------------------------------------------------------

/** "(305) 555-0100" as the patient types. */
const formatPhone = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/**
 * Contact details (moved here from the old registration wizard, 2026-10-05), then the two
 * eligibility questions. Age is not asked: sign-up took the date of birth, and the server checks
 * the record. Location IS asked although sign-up took a Florida ZIP — the package is explicit that
 * an address is not where the patient is right now. Medicare is asked because nothing else tells us.
 */
function EligibilityStep({
  firstName,
  initial,
  onDone,
  supportContact,
}: {
  firstName?: string;
  initial?: EnrollmentContact;
  onDone: (s: EnrollmentState) => void;
  supportContact?: string | null;
}) {
  const api = useApi();
  const [contact, setContact] = useState<Required<{ [K in keyof EnrollmentContact]: string }>>({
    address1: initial?.address1 ?? "",
    address2: initial?.address2 ?? "",
    city: initial?.city ?? "",
    state: initial?.state ?? "FL",
    zip: initial?.zip ?? "",
    phone: initial?.phone ? formatPhone(initial.phone) : "",
  });
  const [inFlorida, setInFlorida] = useState<boolean | null>(null);
  const [medicare, setMedicare] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const setField = (key: keyof typeof contact) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setContact((c) => ({ ...c, [key]: key === "phone" ? formatPhone(e.target.value) : e.target.value }));

  // A complete ZIP fills in city and state, as the registration wizard did.
  useEffect(() => {
    const zip = contact.zip.trim();
    if (!/^\d{5}$/.test(zip)) return;
    let cancelled = false;
    api
      .get<{ found: boolean; city?: string; state?: string }>(`/zipcode/lookup/${zip}`)
      .then(({ data }) => {
        if (cancelled || !data.found) return;
        setContact((c) => ({ ...c, city: c.city || data.city || "", state: data.state || c.state }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.zip]);

  const contactComplete =
    contact.address1.trim() && contact.city.trim() && contact.state && /^\d{5}$/.test(contact.zip.trim()) &&
    contact.phone.replace(/\D/g, "").length === 10;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(
        await enrollmentApi.eligibility({
          physicallyInFlorida: inFlorida === true,
          hasMedicare: medicare === true,
          ...contact,
        }),
      );
    } catch (e) {
      setError((e as EnrollmentError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <StepHeading
        title={firstName ? `Welcome, ${firstName}. Let's get you set up.` : "Let's get you set up"}
        subtitle="Your contact details and two quick questions. This takes about a minute."
      />

      <Stack spacing={4}>
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Your contact details
          </Typography>
          <Stack spacing={2}>
            <TextField label="Street address" value={contact.address1} onChange={setField("address1")} autoComplete="address-line1" fullWidth required />
            <TextField label="Apartment, suite, etc. (optional)" value={contact.address2} onChange={setField("address2")} autoComplete="address-line2" fullWidth />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="ZIP code"
                value={contact.zip}
                onChange={setField("zip")}
                autoComplete="postal-code"
                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 10 } }}
                sx={{ flex: 1 }}
                required
              />
              <TextField label="City" value={contact.city} onChange={setField("city")} autoComplete="address-level2" sx={{ flex: 2 }} required />
              <TextField
                select
                label="State"
                value={contact.state}
                onChange={setField("state")}
                sx={{ flex: 1, minWidth: 110 }}
                required
                slotProps={{ select: { native: true } }}
              >
                {usaStates.map((st) => (
                  <option key={st.abbreviation} value={st.abbreviation}>
                    {st.abbreviation}
                  </option>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Mobile phone"
              value={contact.phone}
              onChange={setField("phone")}
              autoComplete="tel-national"
              slotProps={{ htmlInput: { inputMode: "tel" } }}
              fullWidth
              required
            />
          </Stack>
        </Box>

        <Box role="radiogroup" aria-labelledby="q-florida">
          <Typography id="q-florida" variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Where are you right now?
          </Typography>
          <Stack spacing={1.5}>
            <ChoiceCard
              selected={inFlorida === true}
              onClick={() => setInFlorida(true)}
              label="I'm in Florida"
              description="And I'll be in Florida when I receive care."
            />
            <ChoiceCard selected={inFlorida === false} onClick={() => setInFlorida(false)} label="I'm outside Florida" />
          </Stack>
        </Box>

        <Box role="radiogroup" aria-labelledby="q-medicare">
          <Typography id="q-medicare" variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Are you covered by Medicare or a Medicare Advantage plan?
          </Typography>
          <Stack spacing={1.5}>
            <ChoiceCard selected={medicare === false} onClick={() => setMedicare(false)} label="No" />
            <ChoiceCard selected={medicare === true} onClick={() => setMedicare(true)} label="Yes" />
          </Stack>
        </Box>

        <Panel tone="warning">
          <Stack direction="row" spacing={1.5}>
            <EmergencyOutlinedIcon color="error" aria-hidden />
            <Typography variant="body2">
              <strong>TeleHeadache is not emergency care.</strong> For a sudden severe headache, weakness, numbness,
              trouble speaking, a seizure, fainting, or a headache after a head injury, call 911 or go to the nearest
              emergency room.
            </Typography>
          </Stack>
        </Panel>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mt: 3 }} role="alert">
          {error} {supportContact && <>Questions? Contact {supportContact}.</>}
        </Alert>
      )}

      <StepActions>
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={primaryButtonSx}
          onClick={submit}
          disabled={busy || !contactComplete || inFlorida === null || medicare === null}
        >
          {busy ? "Saving…" : "Continue"}
        </Button>
      </StepActions>
    </Box>
  );
}

// ---- 2. electronic paperwork ------------------------------------------------------------------------

function ElectronicPaperworkStep({ onDone, onBack }: { onDone: (s: EnrollmentState) => void; onBack: () => void }) {
  const [doc, setDoc] = useState<ConsentDocument | null>(null);
  const [reading, setReading] = useState(false);
  const [openedSample, setOpenedSample] = useState(false);
  const [accessConfirmed, setAccessConfirmed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [smsAlerts, setSmsAlerts] = useState(false);
  const [sms, setSms] = useState<SmsDisclosure | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    enrollmentApi
      .electronicPaperwork()
      .then(setDoc)
      .catch((e: EnrollmentError) => setError(e.message));
    // SMS-8: "Text me" is a text-message consent, so it shows the same disclosure the carrier was
    // given. Without it the box is not offered — email alerts still are.
    enrollmentApi
      .smsDisclosure()
      .then(setSms)
      .catch(() => setSms(null));
  }, []);

  const openSample = async () => {
    try {
      await openPdf("/enrollment/sample.pdf");
      setOpenedSample(true);
    } catch {
      setError("We could not open the sample PDF. Contact us for another way to receive your documents.");
    }
  };

  const submit = async () => {
    if (!doc) return;
    setBusy(true);
    setError(null);
    try {
      onDone(
        await enrollmentApi.agreeElectronicPaperwork({
          artifactId: doc.artifactId,
          samplePdfAccessConfirmed: accessConfirmed,
          electronicPaperworkAgreed: agreed,
          emailAlerts,
          smsAlerts: smsAlerts && !!sms,
          smsDisclosureVersion: smsAlerts && sms ? sms.version : undefined,
        }),
      );
    } catch (e) {
      setError((e as EnrollmentError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink onClick={onBack} />
      <StepHeading
        title="Go paperless"
        subtitle="Your agreements and records are delivered electronically, to your secure account."
      />

      <Stack spacing={3}>
        <Panel>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <DescriptionOutlinedIcon color="action" aria-hidden />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Electronic paperwork disclosure</Typography>
              <Typography variant="body2" color="text.secondary">
                What electronic signatures mean, and how to get paper copies.
              </Typography>
            </Box>
            <Button disabled={!doc} onClick={() => setReading(true)} sx={{ textTransform: "none", fontWeight: 600 }}>
              Read
            </Button>
          </Stack>
        </Panel>

        {/* E-SIGN (15 U.S.C. §7001(c)(1)(C)(ii)): consent must reasonably demonstrate the patient
            can open the format records arrive in — so they open a real PDF first. */}
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Check that you can open PDFs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Your signed documents are PDFs. Open this one-page sample to make sure your device can show them.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="outlined"
              onClick={openSample}
              startIcon={openedSample ? <CheckCircleIcon /> : <PictureAsPdfOutlinedIcon />}
              color={openedSample ? "success" : "primary"}
              sx={{ alignSelf: "flex-start", borderRadius: 999, textTransform: "none", fontWeight: 600 }}
            >
              {openedSample ? "Sample opened" : "Open sample PDF"}
            </Button>
            <ChoiceCard
              kind="checkbox"
              disabled={!openedSample}
              selected={accessConfirmed}
              onClick={() => setAccessConfirmed((v) => !v)}
              label="I could read it, and I can save or print it"
            />
          </Stack>
        </Box>

        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Your agreement
          </Typography>
          <ChoiceCard
            kind="checkbox"
            selected={agreed}
            onClick={() => setAgreed((v) => !v)}
            label="I agree to electronic signatures and records"
            description="I'll receive my records and agreements electronically. I can ask for paper copies at any time."
          />
        </Box>

        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Alerts <Typography component="span" color="text.secondary">(optional)</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            A short note when something new is waiting in your account — never health details. Change these anytime.
          </Typography>
          <Stack spacing={1.5}>
            <ChoiceCard kind="checkbox" selected={emailAlerts} onClick={() => setEmailAlerts((v) => !v)} label="Email me" />
            {sms && (
              <ChoiceCard
                kind="checkbox"
                selected={smsAlerts}
                onClick={() => setSmsAlerts((v) => !v)}
                label="Text me"
                description={sms.disclosure}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mt: 3 }} role="alert">
          {error}
        </Alert>
      )}

      <StepActions>
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={primaryButtonSx}
          onClick={submit}
          disabled={busy || !doc || !accessConfirmed || !agreed}
        >
          {busy ? "Saving…" : "Agree & continue"}
        </Button>
      </StepActions>
      <DocumentReviewDialog document={reading ? doc : null} onClose={() => setReading(false)} />
    </Box>
  );
}

// ---- 3. plan + review & sign --------------------------------------------------------------------------

function PlanAndSignStep({ onSigned, onBack }: { onSigned: () => void; onBack: () => void }) {
  const { plans, failed } = useSubscriptionPlans();
  const [planId, setPlanId] = useState<string | null>(null);
  const [review, setReview] = useState<EnrollmentReview | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selected = planId ?? defaultPlanId(plans);

  const startReview = async (id: string | null = selected) => {
    if (!id) return;
    setBusy(true);
    try {
      setReview(await enrollmentApi.review(id));
      window.scrollTo({ top: 0 });
    } catch (e) {
      setNotice((e as EnrollmentError).message);
    } finally {
      setBusy(false);
    }
  };

  if (review) {
    return (
      <>
        {notice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        <BackLink
          label="Back to plans"
          onClick={() => {
            setReview(null);
            setNotice(null);
          }}
        />
        <ReviewAndSign
          review={review}
          onSigned={onSigned}
          onChangePlan={() => {
            setReview(null);
            setNotice(null);
          }}
          onStale={(message) => {
            setNotice(`${message} The documents below have been refreshed.`);
            void startReview(review.planId);
          }}
        />
      </>
    );
  }

  return (
    <Box>
      <BackLink onClick={onBack} />
      <StepHeading
        title="Choose your membership"
        subtitle="Every membership includes the same care. You'll review the exact charge, dates and documents before you sign — nothing is charged until after."
      />
      <PlanPicker plans={plans} failed={failed} selectedPlanId={selected} onSelect={setPlanId} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Uninsured or not using insurance?{" "}
        <MuiLink component={RouterLink} to="/good-faith-estimate">
          Request a Good Faith Estimate
        </MuiLink>
      </Typography>
      {notice && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <StepActions>
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={primaryButtonSx}
          onClick={() => startReview()}
          disabled={busy || !selected}
        >
          {busy ? "Preparing your documents…" : "Continue"}
        </Button>
      </StepActions>
      <BusyOverlay open={busy} message="Preparing your documents…" />
    </Box>
  );
}

// ---- 4. payment ----------------------------------------------------------------------------------------

const POLL_MS = 3000;
const POLL_LIMIT = 20;

function PaymentStep({ state, onRefresh }: { state: EnrollmentState; onRefresh: () => Promise<void> }) {
  const returnedFromStripe = new URLSearchParams(window.location.search).get("paid") === "1";
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [polls, setPolls] = useState(0);

  // Back from Stripe: the webhook activates membership, usually within seconds. Poll briefly.
  useEffect(() => {
    if (!returnedFromStripe || state.status !== "FullyExecuted" || polls >= POLL_LIMIT) return;
    const timer = window.setTimeout(async () => {
      await onRefresh();
      setPolls((n) => n + 1);
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [returnedFromStripe, state.status, polls, onRefresh]);

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await enrollmentApi.checkout();
      window.location.assign(url);
    } catch (e) {
      const err = e as EnrollmentError;
      setError(err.message);
      if (err.code === "REAUTHORIZE") await onRefresh();
      setBusy(false);
    }
  };

  if (state.status === "CountersignPending") {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <HourglassTopIcon sx={{ fontSize: 56, color: "primary.main", mb: 2 }} aria-hidden />
        <StepHeading
          title="You're signed — one last signature to go"
          subtitle="Your membership agreement is waiting for the Practice's signature. You won't be charged until it's signed, and we'll email you as soon as it is."
        />
      </Box>
    );
  }

  if (state.status === "Active") {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} aria-hidden />
        <StepHeading title="You're in!" subtitle="Your membership is active. Taking you to your account…" />
      </Box>
    );
  }

  const confirming = returnedFromStripe && polls < POLL_LIMIT;

  return (
    <Box>
      <StepHeading
        title={confirming ? "Confirming your payment…" : "Last step: payment"}
        subtitle={
          confirming
            ? "This usually takes a few seconds."
            : "Your documents are signed by you and the Practice. Copies are in My Documents → Consents & Agreements."
        }
      />

      {confirming ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress aria-label="Confirming payment" />
        </Box>
      ) : (
        <>
          {returnedFromStripe && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              We haven&apos;t received payment confirmation yet. If you completed payment, refresh in a minute.
              Otherwise, continue to payment below.
            </Alert>
          )}
          <Panel>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <LockOutlinedIcon color="action" aria-hidden />
              <Typography variant="body2">
                You&apos;ll enter your card on Stripe&apos;s secure payment page. TeleHeadache never sees your full card
                number.
              </Typography>
            </Stack>
          </Panel>
          {error && (
            <Alert severity="warning" sx={{ mt: 2 }} role="alert">
              {error}
            </Alert>
          )}
          <StepActions>
            <Button variant="contained" size="large" fullWidth sx={primaryButtonSx} onClick={pay} disabled={busy}>
              {busy ? "Opening secure checkout…" : "Continue to secure payment"}
            </Button>
          </StepActions>
          <BusyOverlay open={busy} message="Opening secure checkout…" />
        </>
      )}
    </Box>
  );
}

// ---- 5. health history (after payment) ---------------------------------------------------------------

/**
 * The clinical intake, now the last part of the one enrollment flow rather than a second wizard
 * (2026-10-05): medical history, then the headache medications where that module is on. Reuses the
 * registration wizard's own steps, so the questions — and what is saved — are unchanged. Saved in
 * one POST /patient at the end, as before; that call also carries the contact details from step 1,
 * because it writes every contact field it is given.
 */
function HealthHistoryStep({ contact }: { contact?: EnrollmentContact }) {
  const api = useApi();
  const navigate = useNavigate();
  const { refreshUserData } = useCerberus();
  const enabledModules = useEnabledModules();
  const withMedications = enabledModules.includes("headache-questionnaire");
  const [page, setPage] = useState(0);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [page]);

  const save = async (last: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await api.post("/patient", {
        address1: contact?.address1 ?? "",
        address2: contact?.address2 ?? "",
        city: contact?.city ?? "",
        state: contact?.state ?? "",
        zip: contact?.zip ?? "",
        phone: contact?.phone ?? "",
        ...values,
        ...last,
      });
      await refreshUserData();
      navigate("/dashboard", { replace: true });
    } catch {
      setError("We couldn't save your health history. Please try again.");
      setBusy(false);
    }
  };

  const isLastPage = page === 1 || !withMedications;

  return (
    <Box>
      <Box
        sx={(theme) => ({
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.success.main, 0.08),
          border: 1,
          borderColor: alpha(theme.palette.success.main, 0.3),
        })}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <CheckCircleIcon color="success" aria-hidden />
          <Typography variant="body2">
            <strong>You&apos;re a member.</strong> One last step: your health history, so your clinician has what they need.
          </Typography>
        </Stack>
      </Box>

      {page === 0 && (
        <>
          <StepHeading title="Your medical history" subtitle="Conditions, allergies and the medications you take today." />
          <RegistrationMedicalHistory
            values={values}
            submitButtonText={isLastPage ? "Finish" : "Next"}
            onNext={(v) => {
              if (isLastPage) void save(v);
              else {
                setValues((current) => ({ ...current, ...v }));
                setPage(1);
              }
            }}
          />
        </>
      )}
      {page === 1 && withMedications && (
        <>
          <StepHeading title="Headache medications" subtitle="Which of these have you tried before?" />
          <RegistrationMedications
            values={values}
            submitButtonText="Finish"
            onBack={(v) => {
              setValues((current) => ({ ...current, ...v }));
              setPage(0);
            }}
            onNext={(v) => void save(v)}
          />
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} role="alert">
          {error}
        </Alert>
      )}
      <BusyOverlay open={busy} message="Saving your health history…" />
    </Box>
  );
}
